import { CatalogDiscovery } from './CatalogDiscovery';
import { CatalogInventory } from './CatalogInventory';
import { DirectCatalogLinking } from './DirectCatalogLinking';
import { DestinationOrder } from './DestinationOrder';
import { CONFIG } from '@frontend/config';
import { routes } from '@frontend/router/routes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReviewSources } from './ReviewSources';
import { AddSelection } from './AddSelection';
import { ImportJobs } from './ImportJobs';
import { ImportHotelCodes } from './ImportHotelCodes';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { catalogAdminPath } from './queries';
import { SelectionDialog } from './SelectionDialog';
import { useState } from 'react';
import {
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from '@frontend/features/common/services';
import {
  catalogAdminKey,
  editPayload,
  isSelectionConflict,
  Kind,
  SelectionPage,
  SelectionRow,
  useCatalogAdminCapability,
} from './queries';

export function CatalogAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const capability = useCatalogAdminCapability();
  const [params, setParams] = useSearchParams();
  const view = params.get('catalog_view') === 'discovery' ? 'discovery' : 'catalog';
  const [discoveryVisited, setDiscoveryVisited] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [codesBusy, setCodesBusy] = useState(false);
  const [directBusy, setDirectBusy] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  if (capability.isLoading) return <Typography>{t('hotelCatalogAdmin.loading')}</Typography>;
  if (!capability.data?.allowed) return <Alert severity="info">{t('hotelCatalogAdmin.unavailable')}</Alert>;
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Button
        size="small"
        disabled={codesBusy || directBusy}
        onClick={() =>
          !codesBusy &&
          !directBusy &&
          navigate(
            `${routes.dashboard.agencyView.root.replace(':id', String(CONFIG.AGENCY_ID))}/${routes.dashboard.agencyView.hotels}`,
          )
        }
      >
        {t('hotelDestinationOrder.backToAgency')}
      </Button>
      <Typography variant="h4" sx={{ mt: 1 }}>
        {t('hotelInventory.title')}
      </Typography>
      <Tabs
        value={view}
        onChange={(_, value) => {
          setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set('catalog_view', value);
            return next;
          });
          if (value === 'discovery') setDiscoveryVisited(true);
        }}
        sx={{ mt: 2 }}
      >
        <Tab value="catalog" label={t('hotelDiscovery.catalogTab')} disabled={codesBusy || directBusy} />
        <Tab value="discovery" label={t('hotelDiscovery.discoveryTab')} disabled={codesBusy || directBusy} />
      </Tabs>
      <Box hidden={view !== 'discovery'} sx={{ mt: 3 }}>
        {(discoveryVisited || view === 'discovery') && <CatalogDiscovery active={view === 'discovery'} />}
      </Box>
      <Box hidden={view !== 'catalog'}>
        <Box sx={{ mt: 2 }}>
          <CatalogInventory />
        </Box>
        <Accordion
          expanded={operationsOpen}
          onChange={(_, expanded) => {
            if (!codesBusy && !directBusy) setOperationsOpen(expanded);
          }}
          sx={{ mt: 3 }}
        >
          <AccordionSummary disabled={codesBusy || directBusy} expandIcon={<ExpandMoreIcon />}>
            <Typography>{t('hotelInventory.tools')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {operationsOpen && (
              <>
                <Alert severity="info" sx={{ my: 2 }}>
                  {t('hotelCatalogAdmin.globalNotice')}
                </Alert>
                <DestinationOrder />
                <DirectCatalogLinking onBusyChange={setDirectBusy} />
                <Accordion
                  expanded={codesOpen}
                  onChange={(_, expanded) => {
                    if (!codesBusy && !directBusy) setCodesOpen(expanded);
                  }}
                  sx={{ mt: 3 }}
                >
                  <AccordionSummary disabled={codesBusy || directBusy} expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t('hotelCodeImport.title')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>{codesOpen && <ImportHotelCodes onBusyChange={setCodesBusy} />}</AccordionDetails>
                </Accordion>
                <Accordion expanded={toolsOpen} onChange={(_, expanded) => setToolsOpen(expanded)} sx={{ mt: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t('hotelDestinationOrder.otherTools')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>{toolsOpen && <CatalogSelectionTools />}</AccordionDetails>
                </Accordion>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}

export function CatalogSelectionTools() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const capability = useCatalogAdminCapability();
  const [kind, setKind] = useState<Kind>('place-mappings');
  const [offset, setOffset] = useState(0);
  const [geoFilter, setGeoFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [selected, setSelected] = useState<SelectionRow | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [rank, setRank] = useState('0');
  const [reason, setReason] = useState('');
  const [conflict, setConflict] = useState(false);
  const cache = useQueryClient();
  const page = useQuery({
    queryKey: [...catalogAdminKey, kind, offset, appliedFilter],
    queryFn: async () =>
      (
        await client.get<SelectionPage>(`hotel-catalog-admin/${kind}`, {
          params: { limit: 50, offset, ...(appliedFilter ? { geo_place_id: appliedFilter } : {}) },
        })
      ).data,
    enabled: capability.data?.allowed === true,
    retry: false,
  });
  const save = useMutation({
    mutationFn: async () => {
      if (!selected || conflict) throw new Error('selection');
      await client.put(`hotel-catalog-admin/${kind}`, editPayload(kind, selected, enabled, Number(rank), reason));
    },
    onSuccess: () => {
      setSelected(null);
      cache.invalidateQueries(catalogAdminKey);
    },
    onError: (error) => {
      if (isSelectionConflict(error)) setConflict(true);
    },
  });
  const choose = (row: SelectionRow) => {
    setSelected(row);
    setEnabled(row.enabled);
    setRank(String(row.rank ?? 0));
    setReason('');
    setConflict(false);
    save.reset();
  };
  if (capability.isLoading) return <Typography>{t('hotelCatalogAdmin.loading')}</Typography>;
  if (!capability.data?.allowed) return <Alert severity="info">{t('hotelCatalogAdmin.unavailable')}</Alert>;
  return (
    <Box sx={{ p: 3 }}>
      <AddSelection />
      <ImportJobs />
      <ReviewSources />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant={kind === 'place-mappings' ? 'contained' : 'outlined'}
          onClick={() => {
            setKind('place-mappings');
            setOffset(0);
          }}
        >
          {t('hotelCatalogAdmin.destinations')}
        </Button>
        <Button
          variant={kind === 'hotel-memberships' ? 'contained' : 'outlined'}
          onClick={() => {
            setKind('hotel-memberships');
            setOffset(0);
          }}
        >
          {t('hotelCatalogAdmin.hotels')}
        </Button>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={t('hotelCatalogAdmin.destinationFilter')}
          value={geoFilter}
          onChange={(event) => setGeoFilter(event.target.value)}
        />
        <Button
          onClick={() => {
            setAppliedFilter(geoFilter.trim());
            setOffset(0);
          }}
        >
          {t('hotelCatalogAdmin.filter')}
        </Button>
      </Stack>
      {page.isError && (
        <Alert severity="error">
          {t('hotelCatalogAdmin.loadError')}{' '}
          <Button onClick={() => page.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
        </Alert>
      )}
      {page.isFetching ? (
        <Typography>{t('hotelCatalogAdmin.loading')}</Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('hotelCatalogAdmin.destinations')}</TableCell>
              <TableCell>
                {t(kind === 'place-mappings' ? 'hotelCatalogAdmin.externalPlace' : 'hotelCatalogAdmin.hotels')}
              </TableCell>
              <TableCell>{t('hotelCatalogAdmin.enabled')}</TableCell>
              <TableCell>{t('hotelCatalogAdmin.rank')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {page.data?.data.map((row) => (
              <TableRow key={JSON.stringify([row.geo_place_id, row.place_id, row.hotel_id])}>
                <TableCell>
                  {row.geo_place_name}
                  <Typography variant="caption" display="block">
                    {row.geo_place_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.hotel_display_name ?? row.place_id}
                  <Typography variant="caption" display="block">
                    {row.hotel_id ?? row.source}
                  </Typography>
                </TableCell>
                <TableCell>{t(row.enabled ? 'hotelCatalogAdmin.yes' : 'hotelCatalogAdmin.no')}</TableCell>
                <TableCell>{row.rank ?? '—'}</TableCell>
                <TableCell>
                  <Button onClick={() => choose(row)}>{t('hotelCatalogAdmin.edit')}</Button>
                  {row.hotel_id && (
                    <Button onClick={() => navigate(`${catalogAdminPath}/hotels/${encodeURIComponent(row.hotel_id!)}`)}>
                      {t('hotelEditorial.title')}
                    </Button>
                  )}
                  {kind === 'place-mappings' && (
                    <Button
                      onClick={() => {
                        setKind('hotel-memberships');
                        setAppliedFilter(row.geo_place_id);
                        setGeoFilter(row.geo_place_id);
                        setOffset(0);
                      }}
                    >
                      {t('hotelCatalogAdmin.hotels')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!page.isFetching && page.data?.data.length === 0 && <Typography>{t('hotelCatalogAdmin.empty')}</Typography>}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button disabled={offset === 0 || page.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 50))}>
          {t('hotelCatalogAdmin.previous')}
        </Button>
        <Button
          disabled={!page.data?.meta.has_more || page.isFetching || offset >= 10000}
          onClick={() => setOffset((v) => v + 50)}
        >
          {t('hotelCatalogAdmin.next')}
        </Button>
      </Stack>
      <SelectionDialog
        selected={selected}
        kind={kind}
        enabled={enabled}
        rank={rank}
        reason={reason}
        conflict={conflict}
        saving={save.isLoading}
        hasError={save.isError}
        setEnabled={setEnabled}
        setRank={setRank}
        setReason={setReason}
        onClose={() => setSelected(null)}
        onReload={() => {
          setSelected(null);
          page.refetch();
        }}
        onSave={() => save.mutate()}
      />
    </Box>
  );
}
